import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';

import { useNotifier, NotifierType } from 'hooks/use-notifier';

import type { UserType } from 'api/api.types';
import type { ReactElement } from 'react';

// 24 hours in seconds - effectively stays until dismissed
const FLASH_NOTIFICATION_DURATION = 86400;
// 3 seconds for auto-dismissing messages (e.g., impersonation exit)
const AUTO_DISMISS_DURATION = 3;

// Pattern to detect impersonation-related flash messages
const IMPERSONATION_MESSAGE_PATTERN = /impersonating/i;

// Flash types that can be auto-dismissed (errors/warnings should persist)
const AUTO_DISMISSABLE_FLASH_TYPES = new Set(['notice', 'success']);

// Maps Rails flash keys to notification types
// Rails uses: notice (success), alert (warning), error
// Also supports: success, warning, info for compatibility
const FLASH_TYPE_MAP: Record<string, NotifierType> = {
  notice: NotifierType.SUCCESS,
  success: NotifierType.SUCCESS,
  alert: NotifierType.WARNING,
  warning: NotifierType.WARNING,
  error: NotifierType.ERROR,
  info: NotifierType.INFO,
};

const FLASH_ICON_MAP: Record<string, ReactElement> = {
  notice: <CheckCircle />,
  success: <CheckCircle />,
  alert: <Warning />,
  warning: <Warning />,
  error: <WarningCircle />,
  info: <Info />,
};

/**
 * Hook to display flash messages from the backend.
 * Flash messages are typically set during redirects from v1 to v2.
 * Shows a persistent toast notification until dismissed by the user.
 */
export function useFlashMessage(user: UserType | null): void {
  const { notify } = useNotifier();
  // Track which specific message was shown to allow new messages in the future
  const shownMessageRef = useRef<string | null>(null);

  useEffect(() => {
    // Reset when flash data is cleared
    if (!user?.flash) {
      shownMessageRef.current = null;
      return;
    }

    const flash = user.flash;

    // Show only the first flash message (notifier can only show one at a time)
    const entries = Object.entries(flash);
    const firstEntry = entries.find(([, message]) => message);

    // Skip if already shown this specific message
    if (!firstEntry || shownMessageRef.current === firstEntry[1]) {
      return;
    }

    const [key, message] = firstEntry;
    shownMessageRef.current = message;

    // Use auto-dismiss for notice/success impersonation messages only
    // Errors and warnings should always persist until dismissed
    const isImpersonationMessage = IMPERSONATION_MESSAGE_PATTERN.test(message);
    const canAutoDismiss = AUTO_DISMISSABLE_FLASH_TYPES.has(key);
    const duration =
      isImpersonationMessage && canAutoDismiss
        ? AUTO_DISMISS_DURATION
        : FLASH_NOTIFICATION_DURATION;

    notify({
      type: FLASH_TYPE_MAP[key] || NotifierType.INFO,
      message,
      duration,
      icon: FLASH_ICON_MAP[key] || <Info />,
      withCloseButton: true,
    });
  }, [user?.flash, notify]);
}
